/**
 * Конфигурация приложения для Browser Control
 * Определяет маршруты и метаданные приложения
 *
 * ВАЖНО: Бизнес-действия (команды) генерируются динамически
 * на основе реального инвентаря в commandGenerator.js
 */
import { config } from './runtimeConfig.js';
import { carTitle } from '../utils/carTitle';
import { getContactParams } from '../utils/contactParams.js';

/**
 * Фоллбэки, когда дилер не настроил ни companyName, ни домен. Нейтральные
 * намеренно: «Cool Cars Amsterdam» — витрина демо, а не имя, которое можно
 * подставить незнакомому тенанту.
 */
const DEFAULT_APP_NAME = 'Car dealership';
const DEFAULT_DOMAIN = 'demo.shiftgears.ai';

/**
 * Генерация CSV списка доступных автомобилей
 * Формат: id,description,mileage,price
 */
export function generateAvailableCarsList(cars) {
  if (!cars || cars.length === 0) {
    return 'id,description,mileage,price\n';
  }

  const header = 'id,description,mileage,price';
  const rows = cars.map(car => {
    const id = car.id || '';
    const description = carTitle(car);
    const mileage = car.mileage || 0;
    const price = car.price || 0;
    return `${id},"${description}",${mileage},${price}`;
  });

  return [header, ...rows].join('\n');
}

/**
 * Маршруты приложения
 */
export const routes = [
  {
    path: '/',
    name: 'Home',
    description: 'Main homepage with featured cars and hot offers'
  },
  {
    path: '/cars',
    name: 'Cars Inventory',
    description: 'Browse all available cars with search and filters'
  },
  {
    path: '/cars/:id',
    name: 'Car Details',
    description: 'Detailed information about a specific car',
    params: ['id']
  },
  {
    path: '/about',
    name: 'About Us',
    description: 'Information about the dealership'
  },
  {
    path: '/contact',
    name: 'Contact',
    description: 'Contact form and dealership information'
  }
];

/**
 * Метаданные приложения
 * ВАЖНО: inventoryId использует getter, т.к. config.INVENTORY_ID может быть недоступен
 * при загрузке модуля (до вызова B2BDemo.mount() который устанавливает b2bMountParams)
 *
 * #1323: appName / domain / contact тоже были статикой — «Cool Cars Amsterdam»,
 * `demo.shiftgears.ai` и британский номер уезжали в browser-control КАЖДОМУ
 * тенанту, потому что этот объект целиком попадает в бандл виджета. Агент у
 * дилера получал чужую вывеску и чужой телефон. Теперь всё это геттеры на тех
 * же источниках, из которых UI берёт контакты (widget config → iframe → URL),
 * а фоллбэк не выдумывает дилера: пусто лучше, чем неверно.
 * `generateAppConfig` разворачивает metadata спредом, а спред вычисляет
 * геттеры — payload остаётся плоским, как и был.
 */
export const metadata = {
  get appName() { return getContactParams().companyName || DEFAULT_APP_NAME; },
  get domain() {
    return typeof window !== 'undefined' && window.location?.hostname
      ? window.location.hostname
      : DEFAULT_DOMAIN;
  },
  description: 'Car dealership website for browsing and booking test drives',
  // Динамический getter вместо статического присваивания (#490)
  get inventoryId() { return config.INVENTORY_ID; },
  features: [
    'Car search and filtering',
    'Detailed car information',
    'Test drive booking',
    'WhatsApp and phone contact',
    'Voice assistant integration'
  ],
  get contact() {
    const params = getContactParams();
    return {
      phone: params.contactPhone || '',
      whatsapp: params.contactWhatsapp || '',
      telegram: params.contactTelegram || '',
      email: params.contactEmail || ''
    };
  }
};

/**
 * Генерирует полную конфигурацию приложения с данными об автомобилях
 * @param {Array} cars - Массив автомобилей из API
 * @returns {Object} Полная конфигурация приложения
 */
export function generateAppConfig(cars = []) {
  return {
    routes,
    metadata: {
      ...metadata,
      availableCarsList: generateAvailableCarsList(cars),
      totalCars: cars.length
    }
  };
}

/**
 * Базовая конфигурация приложения для Browser Control
 */
export const appConfig = {
  routes,
  metadata
};

export default appConfig;
